import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar, Stack } from '@mui/material';
import {
    ContentDto,
    ContentItemDto,
    IConcertAdminDto,
    IConcertSeriesAdminDto,
    ILocationAdminDto,
    IStoredFileDto,
    ImageDto,
} from '../../clients/services';
import { DateTime } from 'luxon';
import { MainContent } from '../../components/MainContent';
import { MainNarrow } from '../../components/MainNarrow';
import ConcertPreview from '../../components/ConcertPreview/ConcertPreview';
import { useAuth } from '../../auth/auth-provider';
import { ConcertSeriesWorkbenchService } from '../../services/concert-series-workbench.service';
import { SeriesListPanel } from './SeriesListPanel';
import { SeriesDetailPanel } from './SeriesDetailPanel';
import { ConcertDetailPanel } from './ConcertDetailPanel';
import { MediaPickerDialog } from '../AdminShared/MediaPickerDialog';
import { PlanEditor } from '../PlansAdmin/PlanEditor';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const service = new ConcertSeriesWorkbenchService();
const serviceUrl = (process.env.REACT_APP_SERVICE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

type Mode = 'series' | 'concert';
type EditorMode = 'edit' | 'view';
type MediaTarget = 'seriesBanner' | 'seriesPicture' | 'concertImage' | 'seriesContentPicture';
type ConcertSidePanel = 'series' | 'plan';

const resolvePublicUrl = (file: IStoredFileDto): string => {
    const rawUrl = file.publicUrl ?? '';
    if (!rawUrl) {
        return '';
    }

    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
        return rawUrl;
    }

    const normalizedPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
    if (!serviceUrl) {
        return normalizedPath;
    }

    return `${serviceUrl}${normalizedPath}`;
};

export const ConcertSeriesWorkbench: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { seriesId: seriesIdParam, concertId: concertIdParam } = useParams();

    const { profile, hasRole } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [series, setSeries] = useState<IConcertSeriesAdminDto[]>([]);
    const [concerts, setConcerts] = useState<IConcertAdminDto[]>([]);
    const [locations, setLocations] = useState<ILocationAdminDto[]>([]);

    const [mode, setMode] = useState<Mode>(concertIdParam ? 'concert' : 'series');
    const [editorMode, setEditorMode] = useState<EditorMode>('edit');
    const [selectedSeriesId, setSelectedSeriesId] = useState<number | undefined>();
    const [selectedConcertId, setSelectedConcertId] = useState<number | undefined>();
    const [concertSidePanel, setConcertSidePanel] = useState<ConcertSidePanel>('series');

    const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
    const [mediaFiles, setMediaFiles] = useState<IStoredFileDto[]>([]);
    const [mediaTarget, setMediaTarget] = useState<MediaTarget>('seriesBanner');
    const [seriesContentPictureIndex, setSeriesContentPictureIndex] = useState<number | null>(null);

    const routeSeriesId = useMemo(() => {
        const parsed = seriesIdParam ? Number.parseInt(seriesIdParam, 10) : NaN;
        return Number.isInteger(parsed) ? parsed : null;
    }, [seriesIdParam]);

    const routeConcertId = useMemo(() => {
        const parsed = concertIdParam ? Number.parseInt(concertIdParam, 10) : NaN;
        return Number.isInteger(parsed) ? parsed : null;
    }, [concertIdParam]);

    const isAllowed = hasRole(profile, 'admin');

    const selectedSeries = useMemo(
        () => series.find((item) => item.id === selectedSeriesId),
        [series, selectedSeriesId],
    );

    const selectedConcert = useMemo(
        () => concerts.find((item) => item.id === selectedConcertId),
        [concerts, selectedConcertId],
    );

    const concertsForSeries = useMemo(() => {
        if (!selectedSeriesId) {
            return [];
        }

        return concerts
            .filter((item) => item.concertSerieId === selectedSeriesId)
            .sort((a, b) => {
                const aScore = a.date ? a.date.toMillis() : 0;
                const bScore = b.date ? b.date.toMillis() : 0;
                return aScore - bScore;
            });
    }, [concerts, selectedSeriesId]);

    const previewSeries = useMemo(() => {
        if (!selectedSeries) {
            return undefined;
        }

        return {
            ...selectedSeries,
            hide: false,
            hideConcerts: false,
            concerts: concertsForSeries.map((concert) => ({
                ...concert,
                hide: false,
            })),
        } as any;
    }, [selectedSeries, concertsForSeries]);

    const loadAll = async () => {
        setIsLoading(true);
        setError('');
        try {
            const [loadedSeries, loadedConcerts, loadedLocations] = await Promise.all([
                service.loadSeriesAsync(),
                service.loadConcertsAsync(),
                service.loadLocationsAsync(),
            ]);

            setSeries(loadedSeries);
            setConcerts(loadedConcerts);
            setLocations(loadedLocations);

            if (!selectedSeriesId && loadedSeries.length > 0) {
                setSelectedSeriesId(loadedSeries[0].id);
            }
        } catch (e) {
            setError('Daten konnten nicht geladen werden.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAllowed) {
            return;
        }

        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllowed]);

    useEffect(() => {
        if (!series.length) {
            return;
        }

        const fallbackSeries = selectedSeriesId ? series.find((item) => item.id === selectedSeriesId) : undefined;
        const selectedSeriesFromRoute =
            routeSeriesId !== null ? series.find((item) => item.id === routeSeriesId) : undefined;
        const resolvedSeries = fallbackSeries ?? selectedSeriesFromRoute ?? series[0];

        if (selectedSeriesId !== resolvedSeries.id) {
            setSelectedSeriesId(resolvedSeries.id);
        }

        if (editorMode === 'view') {
            if (selectedConcertId !== undefined) {
                setSelectedConcertId(undefined);
            }
            if (mode !== 'series') {
                setMode('series');
            }
            return;
        }

        const selectedConcertForSeries = selectedConcertId
            ? concerts.find((item) => item.id === selectedConcertId && item.concertSerieId === resolvedSeries.id)
            : undefined;
        const allowRouteConcertSelection = mode === 'concert' || selectedConcertId !== undefined || !selectedSeriesId;
        const selectedConcertFromRoute =
            routeConcertId !== null && allowRouteConcertSelection
                ? concerts.find((item) => item.id === routeConcertId && item.concertSerieId === resolvedSeries.id)
                : undefined;
        const resolvedConcert = selectedConcertForSeries ?? selectedConcertFromRoute;

        if (resolvedConcert) {
            if (selectedConcertId !== resolvedConcert.id) {
                setSelectedConcertId(resolvedConcert.id);
            }
            if (mode !== 'concert') {
                setMode('concert');
            }
            return;
        }

        if (selectedConcertId !== undefined) {
            setSelectedConcertId(undefined);
        }
        if (mode !== 'series') {
            setMode('series');
        }
    }, [concerts, editorMode, mode, routeConcertId, routeSeriesId, selectedConcertId, selectedSeriesId, series]);

    useEffect(() => {
        if (!selectedSeriesId) {
            return;
        }

        if (editorMode === 'view') {
            const viewPath = `/admin/concert-series/${selectedSeriesId}`;
            if (location.pathname !== viewPath) {
                navigate(viewPath, { replace: true });
            }
            return;
        }

        const selectedConcertForSeries = selectedConcertId
            ? concerts.find((item) => item.id === selectedConcertId && item.concertSerieId === selectedSeriesId)
            : undefined;

        const targetPath =
            mode === 'concert' && selectedConcertForSeries
                ? `/admin/concert-series/${selectedSeriesId}/concert/${selectedConcertForSeries.id}`
                : `/admin/concert-series/${selectedSeriesId}`;

        if (location.pathname !== targetPath) {
            navigate(targetPath, { replace: true });
        }
    }, [concerts, editorMode, location.pathname, mode, navigate, selectedConcertId, selectedSeriesId]);

    const backToSeriesMode = () => {
        if (selectedSeriesId) {
            navigate(`/admin/concert-series/${selectedSeriesId}`, { replace: true });
        }
        setSelectedConcertId(undefined);
        setMode('series');
        setConcertSidePanel('series');
    };

    const updateSeriesInState = (updated: IConcertSeriesAdminDto) => {
        setSeries((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    };

    const updateConcertInState = (updated: IConcertAdminDto) => {
        setConcerts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    };

    const persistSeriesVisibilityChange = async (updated: IConcertSeriesAdminDto, successMessage: string) => {
        try {
            const saved = await service.updateSeriesAsync(updated.id, service.toSeriesUpsert(updated));
            updateSeriesInState(saved);
            setSuccess(successMessage);
        } catch {
            setError('Serie konnte nicht gespeichert werden.');
        }
    };

    const updateSelectedSeriesParagraphImage = (paragraphIndex: number, image: ImageDto | null) => {
        if (!selectedSeries) {
            return;
        }

        const content = selectedSeries.content
            ? new ContentDto(selectedSeries.content)
            : new ContentDto({ paragraphs: [], highlight: [] });
        const paragraphs = [...(content.paragraphs ?? [])];

        if (paragraphIndex < 0 || paragraphIndex >= paragraphs.length) {
            return;
        }

        const paragraph = new ContentItemDto(paragraphs[paragraphIndex]);
        paragraph.url = image?.url ?? undefined;
        paragraph.title = image?.title ?? paragraph.title ?? undefined;
        paragraphs[paragraphIndex] = paragraph;

        updateSeriesInState({
            ...selectedSeries,
            content: new ContentDto({ ...content, paragraphs }),
        });
    };

    const openMediaPicker = async (target: MediaTarget) => {
        setMediaTarget(target);
        try {
            await refreshMediaFiles();
            setIsMediaPickerOpen(true);
        } catch (e) {
            setError('Medienbibliothek konnte nicht geladen werden.');
        }
    };

    const refreshMediaFiles = async () => {
        const files = await service.listMediaImagesAsync();
        setMediaFiles(files);
    };

    const uploadMediaFromDialog = async (file: File): Promise<boolean> => {
        try {
            await service.uploadMediaImageAsync(file);
            setSuccess('Datei hochgeladen.');
            return true;
        } catch {
            setError('Upload fehlgeschlagen.');
            return false;
        }
    };

    const applyImageByTarget = async (target: MediaTarget, image: ImageDto) => {
        if (!selectedSeries) {
            return;
        }

        if (target === 'seriesBanner') {
            const updated = await service.setSeriesBannerAsync(selectedSeries.id, image);
            updateSeriesInState(updated);
            setSuccess('Banner aktualisiert.');
            return;
        }

        if (target === 'seriesPicture') {
            const updated = await service.setSeriesPictureAsync(selectedSeries.id, image);
            updateSeriesInState(updated);
            setSuccess('Hauptbild aktualisiert.');
            return;
        }

        if (target === 'concertImage' && selectedConcert) {
            const updated = await service.addConcertImageAsync(selectedConcert.id, image);
            updateConcertInState(updated);
            setSuccess('Konzertbild hinzugefuegt.');
            return;
        }

        if (target === 'seriesContentPicture' && seriesContentPictureIndex !== null) {
            updateSelectedSeriesParagraphImage(seriesContentPictureIndex, image);
            setSuccess('Absatzbild aktualisiert.');
            setSeriesContentPictureIndex(null);
        }
    };

    if (!isAllowed) {
        return (
            <MainContent>
                <Alert severity="warning">Kein Zugriff.</Alert>
            </MainContent>
        );
    }

    if (isLoading) {
        return (
            <MainContent>
                <CircularProgress />
            </MainContent>
        );
    }

    return (
        <MainContent>
            <Stack
                sx={{
                    height: 'calc(100vh - 220px)',
                    position: 'relative',
                    boxSizing: 'border-box',
                    pl: '24px',
                    pr: '24px',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'transparent',
                    color: editorMode === 'view' ? 'inherit' : '#1f1f1f',
                }}
                direction="row"
            >
                {editorMode === 'view' ? (
                    <>
                        <Box sx={{ width: '50%', minWidth: 320, backgroundColor: '#ffffff', color: 'inherit' }}>
                            <SeriesListPanel
                                series={series}
                                selectedSeriesId={selectedSeriesId}
                                editorMode={editorMode}
                                onEditorModeChange={setEditorMode}
                                onSelectSeries={(seriesId) => {
                                    setSelectedSeriesId(seriesId);
                                    setSelectedConcertId(undefined);
                                    setMode('series');
                                }}
                                onCreateSeries={async (title) => {
                                    const created = await service.createSeriesAsync(title);
                                    setSeries((current) => [created, ...current]);
                                    setSelectedSeriesId(created.id);
                                    setMode('series');
                                    setSuccess('Serie erstellt (versteckt).');
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                width: '50%',
                                backgroundColor: 'transparent',
                                color: 'inherit',
                                overflow: 'auto',
                                p: 0,
                                textAlign: 'left',
                            }}
                        >
                            {previewSeries ? (
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        '& #mainnarrow': {
                                            float: 'none',
                                            margin: '0 auto',
                                            textAlign: 'left',
                                        },
                                    }}
                                >
                                    <MainNarrow>
                                        <ConcertPreview {...previewSeries} includeTicketReservationLink />
                                    </MainNarrow>
                                </Box>
                            ) : (
                                <Box sx={{ p: 1.25 }}>
                                    <Alert severity="info">Bitte eine Serie fuer die Vorschau auswaehlen.</Alert>
                                </Box>
                            )}
                        </Box>
                    </>
                ) : mode === 'series' ? (
                    <>
                        <Box sx={{ width: '50%', minWidth: 360, backgroundColor: '#ffffff', color: '#1f1f1f' }}>
                            <SeriesListPanel
                                series={series}
                                selectedSeriesId={selectedSeriesId}
                                editorMode={editorMode}
                                onEditorModeChange={setEditorMode}
                                onSelectSeries={(seriesId) => {
                                    setSelectedSeriesId(seriesId);
                                    setSelectedConcertId(undefined);
                                    setMode('series');
                                }}
                                onCreateSeries={async (title) => {
                                    const created = await service.createSeriesAsync(title);
                                    setSeries((current) => [created, ...current]);
                                    setSelectedSeriesId(created.id);
                                    setMode('series');
                                    setSuccess('Serie erstellt (versteckt).');
                                }}
                            />
                        </Box>

                        <Box sx={{ width: '50%', backgroundColor: '#ffffff', color: '#1f1f1f' }}>
                            <SeriesDetailPanel
                                series={selectedSeries}
                                concerts={concertsForSeries}
                                locations={locations}
                                mode={mode}
                                onSeriesChange={(updated) => updateSeriesInState(updated)}
                                onSeriesVisibilityChange={persistSeriesVisibilityChange}
                                onSaveSeries={async () => {
                                    if (!selectedSeries) {
                                        return;
                                    }

                                    const datedConcerts = concertsForSeries
                                        .map((item) => item.date)
                                        .filter(
                                            (item): item is DateTime => !!item && typeof item.toMillis === 'function',
                                        )
                                        .sort((a, b) => a.toMillis() - b.toMillis());

                                    const seriesWithDerivedDates: IConcertSeriesAdminDto = {
                                        ...selectedSeries,
                                        seriesStart: datedConcerts[0] ?? null,
                                        seriesEnd: datedConcerts.length
                                            ? datedConcerts[datedConcerts.length - 1]
                                            : null,
                                    };

                                    const saved = await service.updateSeriesAsync(
                                        selectedSeries.id,
                                        service.toSeriesUpsert(seriesWithDerivedDates),
                                    );
                                    updateSeriesInState(saved);
                                    setSuccess('Serie gespeichert.');
                                }}
                                onAddConcert={async () => {
                                    if (!selectedSeries) {
                                        return;
                                    }

                                    const created = await service.createConcertAsync(
                                        selectedSeries.id,
                                        selectedSeries.title || 'Neues Konzert',
                                        selectedSeries.ticketSaleStart ?? null,
                                    );
                                    setConcerts((current) => [...current, created]);
                                    setSelectedConcertId(created.id);
                                    setMode('concert');
                                    setConcertSidePanel('series');
                                    setSuccess('Konzert erstellt.');
                                }}
                                onSelectConcert={(concertId) => {
                                    setSelectedConcertId(concertId);
                                    setMode('concert');
                                    setConcertSidePanel('series');
                                }}
                                onSeriesBannerDropped={async (file) => {
                                    const uploaded = await service.uploadMediaImageAsync(file);
                                    await applyImageByTarget(
                                        'seriesBanner',
                                        new ImageDto({
                                            url: resolvePublicUrl(uploaded),
                                            title: uploaded.title || uploaded.fileName,
                                        }),
                                    );
                                }}
                                onSeriesPictureDropped={async (file) => {
                                    const uploaded = await service.uploadMediaImageAsync(file);
                                    await applyImageByTarget(
                                        'seriesPicture',
                                        new ImageDto({
                                            url: resolvePublicUrl(uploaded),
                                            title: uploaded.title || uploaded.fileName,
                                        }),
                                    );
                                }}
                                onSeriesBannerPick={() => openMediaPicker('seriesBanner')}
                                onSeriesPicturePick={() => openMediaPicker('seriesPicture')}
                                onClearSeriesBanner={async () => {
                                    if (!selectedSeries) {
                                        return;
                                    }
                                    const updated = await service.setSeriesBannerAsync(selectedSeries.id, null);
                                    updateSeriesInState(updated);
                                }}
                                onClearSeriesPicture={async () => {
                                    if (!selectedSeries) {
                                        return;
                                    }
                                    const updated = await service.setSeriesPictureAsync(selectedSeries.id, null);
                                    updateSeriesInState(updated);
                                }}
                                onParagraphPictureDropped={async (index, file) => {
                                    const uploaded = await service.uploadMediaImageAsync(file);
                                    updateSelectedSeriesParagraphImage(
                                        index,
                                        new ImageDto({
                                            url: resolvePublicUrl(uploaded),
                                            title: uploaded.title || uploaded.fileName,
                                        }),
                                    );
                                    setSuccess('Absatzbild aktualisiert.');
                                }}
                                onParagraphPicturePick={(index) => {
                                    setSeriesContentPictureIndex(index);
                                    openMediaPicker('seriesContentPicture');
                                }}
                                onClearParagraphPicture={(index) => {
                                    updateSelectedSeriesParagraphImage(index, null);
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <>
                        <Box
                            onClick={() => {
                                if (mode === 'concert' && concertSidePanel === 'series') {
                                    backToSeriesMode();
                                    return;
                                }

                                setConcertSidePanel('series');
                            }}
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 24,
                                cursor: 'pointer',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                userSelect: 'none',
                                zIndex: 2,
                            }}
                            title="<"
                        >
                            <Box component="span" sx={{ fontWeight: 700 }}>
                                {'<'}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                width: concertSidePanel === 'series' ? '50%' : 0,
                                minWidth: concertSidePanel === 'series' ? 320 : 0,
                                overflow: 'hidden',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: '#ffffff',
                            }}
                        >
                            {concertSidePanel === 'series' && (
                                <SeriesDetailPanel
                                    series={selectedSeries}
                                    concerts={concertsForSeries}
                                    locations={locations}
                                    mode={mode}
                                    onSeriesChange={(updated) => updateSeriesInState(updated)}
                                    onSeriesVisibilityChange={persistSeriesVisibilityChange}
                                    onSaveSeries={async () => {
                                        if (!selectedSeries) {
                                            return;
                                        }

                                        const datedConcerts = concertsForSeries
                                            .map((item) => item.date)
                                            .filter(
                                                (item): item is DateTime =>
                                                    !!item && typeof item.toMillis === 'function',
                                            )
                                            .sort((a, b) => a.toMillis() - b.toMillis());

                                        const seriesWithDerivedDates: IConcertSeriesAdminDto = {
                                            ...selectedSeries,
                                            seriesStart: datedConcerts[0] ?? null,
                                            seriesEnd: datedConcerts.length
                                                ? datedConcerts[datedConcerts.length - 1]
                                                : null,
                                        };

                                        const saved = await service.updateSeriesAsync(
                                            selectedSeries.id,
                                            service.toSeriesUpsert(seriesWithDerivedDates),
                                        );
                                        updateSeriesInState(saved);
                                        setSuccess('Serie gespeichert.');
                                    }}
                                    onAddConcert={async () => {
                                        if (!selectedSeries) {
                                            return;
                                        }

                                        const created = await service.createConcertAsync(
                                            selectedSeries.id,
                                            selectedSeries.title || 'Neues Konzert',
                                            selectedSeries.ticketSaleStart ?? null,
                                        );
                                        setConcerts((current) => [...current, created]);
                                        setSelectedConcertId(created.id);
                                        setMode('concert');
                                        setConcertSidePanel('series');
                                        setSuccess('Konzert erstellt.');
                                    }}
                                    onSelectConcert={(concertId) => {
                                        setSelectedConcertId(concertId);
                                        setMode('concert');
                                        setConcertSidePanel('series');
                                    }}
                                    onSeriesBannerDropped={async (file) => {
                                        const uploaded = await service.uploadMediaImageAsync(file);
                                        await applyImageByTarget(
                                            'seriesBanner',
                                            new ImageDto({
                                                url: resolvePublicUrl(uploaded),
                                                title: uploaded.title || uploaded.fileName,
                                            }),
                                        );
                                    }}
                                    onSeriesPictureDropped={async (file) => {
                                        const uploaded = await service.uploadMediaImageAsync(file);
                                        await applyImageByTarget(
                                            'seriesPicture',
                                            new ImageDto({
                                                url: resolvePublicUrl(uploaded),
                                                title: uploaded.title || uploaded.fileName,
                                            }),
                                        );
                                    }}
                                    onSeriesBannerPick={() => openMediaPicker('seriesBanner')}
                                    onSeriesPicturePick={() => openMediaPicker('seriesPicture')}
                                    onClearSeriesBanner={async () => {
                                        if (!selectedSeries) {
                                            return;
                                        }
                                        const updated = await service.setSeriesBannerAsync(selectedSeries.id, null);
                                        updateSeriesInState(updated);
                                    }}
                                    onClearSeriesPicture={async () => {
                                        if (!selectedSeries) {
                                            return;
                                        }
                                        const updated = await service.setSeriesPictureAsync(selectedSeries.id, null);
                                        updateSeriesInState(updated);
                                    }}
                                    onParagraphPictureDropped={async (index, file) => {
                                        const uploaded = await service.uploadMediaImageAsync(file);
                                        updateSelectedSeriesParagraphImage(
                                            index,
                                            new ImageDto({
                                                url: resolvePublicUrl(uploaded),
                                                title: uploaded.title || uploaded.fileName,
                                            }),
                                        );
                                        setSuccess('Absatzbild aktualisiert.');
                                    }}
                                    onParagraphPicturePick={(index) => {
                                        setSeriesContentPictureIndex(index);
                                        openMediaPicker('seriesContentPicture');
                                    }}
                                    onClearParagraphPicture={(index) => {
                                        updateSelectedSeriesParagraphImage(index, null);
                                    }}
                                />
                            )}
                        </Box>

                        <Box
                            sx={{
                                width: concertSidePanel === 'plan' ? 0 : '50%',
                                minWidth: concertSidePanel === 'plan' ? 0 : 420,
                                overflow: 'hidden',
                                backgroundColor: '#ffffff',
                                color: '#1f1f1f',
                            }}
                        >
                            <ConcertDetailPanel
                                concert={selectedConcert}
                                locations={locations}
                                onConcertChange={(updated) => updateConcertInState(updated)}
                                onSaveConcert={async () => {
                                    if (!selectedConcert) {
                                        return;
                                    }

                                    const saved = await service.updateConcertAsync(
                                        selectedConcert.id,
                                        service.toConcertUpsert(selectedConcert),
                                    );
                                    updateConcertInState(saved);
                                    setSuccess('Konzert gespeichert.');
                                }}
                                onDeleteConcert={async () => {
                                    if (!selectedConcert) {
                                        return;
                                    }

                                    const isConfirmed = window.confirm('Konzert wirklich loeschen?');
                                    if (!isConfirmed) {
                                        return;
                                    }

                                    await service.deleteConcertAsync(selectedConcert.id);
                                    setConcerts((current) => current.filter((item) => item.id !== selectedConcert.id));
                                    setSelectedConcertId(undefined);
                                    setMode('series');
                                    setSuccess('Konzert geloescht.');
                                }}
                                onDuplicateConcert={async () => {
                                    if (!selectedConcert || !selectedSeries) {
                                        return;
                                    }

                                    const duplicate = await service.createConcertAsync(
                                        selectedSeries.id,
                                        `${selectedConcert.title} (Kopie)`,
                                    );
                                    const payload = service.toConcertUpsert({
                                        ...selectedConcert,
                                        id: duplicate.id,
                                        title: duplicate.title,
                                    });
                                    const saved = await service.updateConcertAsync(duplicate.id, payload);
                                    setConcerts((current) => [...current, saved]);
                                    setSelectedConcertId(saved.id);
                                    setSuccess('Konzert dupliziert.');
                                }}
                                onConcertImageDropped={async (file) => {
                                    const uploaded = await service.uploadMediaImageAsync(file);
                                    await applyImageByTarget(
                                        'concertImage',
                                        new ImageDto({
                                            url: resolvePublicUrl(uploaded),
                                            title: uploaded.title || uploaded.fileName,
                                        }),
                                    );
                                }}
                                onConcertImagePick={() => openMediaPicker('concertImage')}
                                onRemoveConcertImage={async (url) => {
                                    if (!selectedConcert || !url) {
                                        return;
                                    }

                                    const updated = await service.removeConcertImageAsync(selectedConcert.id, url);
                                    updateConcertInState(updated);
                                }}
                            />
                        </Box>

                        {concertSidePanel !== 'plan' && (
                            <Box
                                onClick={() => setConcertSidePanel('plan')}
                                sx={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: 24,
                                    cursor: 'pointer',
                                    borderLeft: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    userSelect: 'none',
                                    zIndex: 2,
                                }}
                                title=">"
                            >
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                    {'>'}
                                </Box>
                            </Box>
                        )}

                        <Box
                            sx={{
                                position: 'relative',
                                width: concertSidePanel === 'plan' ? '100%' : 0,
                                minWidth: concertSidePanel === 'plan' ? 640 : 0,
                                overflow: 'hidden',
                                borderLeft: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'transparent',
                            }}
                        >
                            {concertSidePanel === 'plan' && (
                                <>
                                    <Box
                                        onClick={() => setConcertSidePanel('series')}
                                        sx={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: 24,
                                            cursor: 'pointer',
                                            borderRight: '1px solid',
                                            borderColor: 'divider',
                                            backgroundColor: '#ffffff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            userSelect: 'none',
                                            zIndex: 3,
                                        }}
                                        title="<"
                                    >
                                        <Box component="span" sx={{ fontWeight: 700 }}>
                                            {'<'}
                                        </Box>
                                    </Box>

                                    <Box sx={{ height: '100%', pl: 3 }}>
                                        <PlanEditor
                                            disableRouteSync
                                            seatPlanBackgroundColor="transparent"
                                            seatPlanTextColor="#ffffff"
                                            allowedLocationId={selectedConcert?.locationId ?? null}
                                            compactSidePanels
                                        />
                                    </Box>
                                </>
                            )}
                        </Box>
                    </>
                )}
            </Stack>

            {error && (
                <Alert sx={{ mt: 1 }} severity="error" onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <MediaPickerDialog
                open={isMediaPickerOpen}
                files={mediaFiles}
                onClose={() => setIsMediaPickerOpen(false)}
                onUpload={uploadMediaFromDialog}
                onRefresh={refreshMediaFiles}
                onPick={async (file) => {
                    await applyImageByTarget(
                        mediaTarget,
                        new ImageDto({
                            url: resolvePublicUrl(file),
                            title: file.title || file.fileName,
                        }),
                    );
                }}
            />

            <Snackbar
                open={success.length > 0}
                autoHideDuration={3000}
                onClose={() => setSuccess('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="success" onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            </Snackbar>
        </MainContent>
    );
};
